
import {
  defineComponent,
  computed,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import VueApexCharts from 'vue3-apexcharts';
import useFormatFunctions from '@/mixins/FormatFunctions';
import ElectricityLabeling from '@/interfaces/ElectricityLabeling';

export default defineComponent({
  components: {
    VueApexCharts,
  },

  setup() {
    const store = useStore();
    const contentRow = ref<InstanceType<typeof HTMLElement>>();
    const scrollContainer = ref<InstanceType<typeof HTMLElement>>();

    store.dispatch('electricityLabelings/get');

    const electricityLabelings = computed<ElectricityLabeling[]>(
      () => store.getters['electricityLabelings/get'],
    );

    const activeElectricityLabelingKey = ref<number | null>(null);
    const activeElectricityLabeling = computed<ElectricityLabeling | null>(() => {
      if (activeElectricityLabelingKey.value !== null) {
        return electricityLabelings.value[activeElectricityLabelingKey.value];
      }

      return null;
    });

    watch(() => electricityLabelings.value, () => {
      if (electricityLabelings.value.length) {
        activeElectricityLabelingKey.value = 0;
      }
    }, { deep: true });

    const chartValues = computed<number[]>(() => {
      if (activeElectricityLabeling.value) {
        return Object.values(activeElectricityLabeling.value.values).splice(0, 6);
      }

      return [];
    });

    const { formatNumber } = useFormatFunctions();

    const labels = [
      'Kernkraft',
      'Kohle',
      'Erdgas',
      'Sonstige fossile Energieträger',
      'Erneuerbare Energien, gefördert nach dem EEG',
      'Strom aus Erneuerbaren Energien mit Herkunftsnachweisen, nicht gefördert nach dem EEG',
    ];

    const chartOptions = ref({
      chart: {
        width: '100%',
        height: 'auto',
        selection: {
          enabled: false,
        },
        parentHeightOffset: 0,
      },
      colors: [
        '#F4AC1F',
        '#EC7D0F',
        '#D9000D',
        '#830008',
        '#4D9DD7',
        '#00468F',
      ],
      legend: {
        show: false,
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          // eslint-disable-next-line no-irregular-whitespace
          formatter: (value: number) => `${formatNumber(value, 1, true)} %`,
          title: {
            formatter: (seriesName: string, data: { seriesIndex: number }) => `${labels[data.seriesIndex]}`,
          },
        },
      },
      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: '55%',
          },
        },
      },
    });

    const isMobile = ref(false);
    const showScrollShadow = ref(false);

    const scrollBarShadowHandler = () => {
      showScrollShadow.value = true;

      if (scrollContainer.value) {
        const {
          offsetWidth,
          scrollWidth,
          scrollLeft,
        } = scrollContainer.value;

        if (offsetWidth < scrollWidth) {
          if ((scrollWidth - offsetWidth) === scrollLeft) {
            showScrollShadow.value = false;
          }
        }
      }
    };

    const mobileHandler = () => {
      isMobile.value = (contentRow.value && contentRow.value.offsetWidth <= 768) as boolean;
    };

    const responsiveEventHandler = () => {
      scrollBarShadowHandler();
      mobileHandler();
    };

    onMounted(() => {
      window.addEventListener('resize', responsiveEventHandler);
      window.addEventListener('load', responsiveEventHandler);
      responsiveEventHandler();
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', responsiveEventHandler);
      window.removeEventListener('load', responsiveEventHandler);
    });

    const changeElectricityLabeling = ($event: Event, key: number) => {
      if ($event.target) {
        const element = $event.target as HTMLElement;
        const parent = element.offsetParent as HTMLElement;

        if ((element.offsetLeft + element.offsetWidth) > parent.offsetWidth) {
          if (scrollContainer.value) {
            scrollContainer.value.scrollLeft += (parent.offsetWidth
              - (element.offsetLeft + element.offsetWidth)
            ) * -1;
          }
        }
      }
      activeElectricityLabelingKey.value = key;
    };

    return {
      electricityLabelings,
      activeElectricityLabeling,
      activeElectricityLabelingKey,
      changeElectricityLabeling,
      formatNumber,
      chartValues,
      contentRow,
      chartOptions,
      labels,
      scrollContainer,
      scrollBarShadowHandler,
      showScrollShadow,
      isMobile,
    };
  },
});
